import { DateTime } from "luxon";

import {
  CalendarEvent,
  CalendarEventAttendee,
  CalendarEventTime,
  CollaboratorAgency,
  CollaboratorAgencyEnriched,
  CollaboratorChatSettings,
  CollaboratorLeave,
  CollaboratorNotification,
  CollaboratorTask,
  SearchContractorCollaboratorsAgencies,
  Vocal,
} from "hassibot/services_v2/collaborator/types";
import { isoDateReader, list, opt } from "hassibot/services_v2/common/reader";
import { Collaborator, JsonValue, LegalEntity, LegalMode } from "hassibot/services_v2/common/types";

/**
 * Temp implem to update the LegalMode enum value without breaking HB
 * Should be kept for the collaborators that have not logout since Sep 8th 2024
 * @deprecated
 */
enum DeprecatedLegalMode {
  // Deprecated values
  PRESTA = "presta",
  MANDAT = "mandat",
  // new values
  CONTRACTOR = LegalMode.CONTRACTOR,
  MANDATARY = LegalMode.MANDATARY,
}
// deprecated -> new values mapping
const DEPRECATED_TO_LEGAL_MODE: Record<DeprecatedLegalMode, LegalMode> = {
  [DeprecatedLegalMode.PRESTA]: LegalMode.CONTRACTOR,
  [DeprecatedLegalMode.MANDAT]: LegalMode.MANDATARY,
  [DeprecatedLegalMode.MANDATARY]: LegalMode.MANDATARY,
  [DeprecatedLegalMode.CONTRACTOR]: LegalMode.CONTRACTOR,
};
const legalEntityReader = (json: JsonValue): LegalEntity => ({
  uuid: json.uuid,
  name: json.name,
  mode: DEPRECATED_TO_LEGAL_MODE[json.mode],
  isSandbox: json.isSandbox ?? false, // default to false for backward compatibility
});

const legalEntitiesReader = list(legalEntityReader);
/** End of temp implem */

export const collaboratorReader = (json: JsonValue): Collaborator => ({
  birthday: opt(isoDateReader)(json.birthday),
  firstDay: opt(isoDateReader)(json.firstDay),
  firstName: json.firstName,
  lastName: json.lastName,
  mail: json.mail,
  dsitJob: json.dsitJob,
  deleted: Boolean(json.deleted),
  phones: (json.phones || []).map(p => ({
    type: p.type,
    value: p.value,
  })),
  pictureUrl: json.pictureUrl,
  place: json.place,
  agencyUuids: json.agencyUuids,
  roles: json.roles,
  googleId: json.googleId,
  featureFlags: json.featureFlags,
  microsoftId: json.microsoftId,
  dsitUuid: json.dsitUuid,
  permerUserAttributes: json.permerUserAttributes,
  myAccessibleLegalEntities: legalEntitiesReader(json.myAccessibleLegalEntities),
});

export const collaboratorsReader = (json: JsonValue): Collaborator[] =>
  json.map(collaboratorReader);

export const collaboratorTaskReader = (json: JsonValue): CollaboratorTask => ({
  uuid: json.uuid,
  type: json.type,
  linkedEntityId: json.linkedEntityId,
  collaboratorDsitUuid: json.collaboratorDsitUuid,
  status: json.status,
  agencyUuid: json.agencyUuid,
  content: json.content,
  createdAt: opt(isoDateReader)(json.createdAt),
  updatedAt: opt(isoDateReader)(json.updatedAt),
  plannedAt: opt(isoDateReader)(json.plannedAt),
});

export const collaboratorTasksReader = (json: JsonValue): CollaboratorTask[] =>
  json.map(collaboratorTaskReader);

export const collaboratorChatSettingsReader = (json: JsonValue): CollaboratorChatSettings => ({
  collaboratorDsitUuid: json.collaboratorDsitUuid,
  subscribedAgencyUuids: json.subscribedAgencyUuids,
  isSilentModeOn: json.isSilentModeOn,
});

export const collaboratorAgencyReader = (json: JsonValue): CollaboratorAgency => {
  return {
    collaboratorDsitUuid: json.collaboratorDsitUuid,
    agencyUuid: json.agencyUuid,
    postingType: json.postingType,
    dsitJob: json.dsitJob,
    startsOn: json.startsOn ? DateTime.fromISO(json.startsOn) : null,
    endsOn: json.endsOn ? DateTime.fromISO(json.endsOn) : null,
  };
};

export const collaboratorAgencyWithDataReader = (json: JsonValue): CollaboratorAgencyEnriched => {
  return {
    ...collaboratorAgencyReader(json),
    collaboratorFirstName: json.collaboratorFirstName,
    collaboratorLastName: json.collaboratorLastName,
    collaboratorPhones: (json.collaboratorPhones || []).map(p => ({
      type: p.type,
      value: p.value,
    })),
    collaboratorMail: json.collaboratorMail,
  };
};

export const collaboratorAgenciesReader = (json: JsonValue): CollaboratorAgency[] =>
  json.map(collaboratorAgencyReader);

export const collaboratorAgenciesEnrichedReader = (json: JsonValue): CollaboratorAgencyEnriched[] =>
  json.map(collaboratorAgencyWithDataReader);

export const searchContractorCollaboratorsAgenciesReader = (
  json: JsonValue
): SearchContractorCollaboratorsAgencies => {
  return {
    collaboratorsAgenciesEnriched: collaboratorAgenciesEnrichedReader(
      json.collaboratorsAgenciesEnriched
    ),
    hasAgency: json.hasAgency,
    hasCoreOrCloseCoreAgency: json.hasCoreOrCloseCoreAgency,
  };
};

export const collaboratorNotificationReader = (json: JsonValue): CollaboratorNotification => ({
  uuid: json.uuid,
  occurredAt: isoDateReader(json.occurredAt),
  data: json.data,
  relatedEntityId: json.relatedEntityId,
  relatedEntityType: json.relatedEntityType,
  notificationType: json.notificationType,
  createdAt: isoDateReader(json.createdAt),
  updatedAt: isoDateReader(json.updatedAt),
  isRead: json.isRead,
});

export const collaboratorNotificationsReader = (json: JsonValue): CollaboratorNotification[] =>
  json.map(collaboratorNotificationReader);

export const vocalReader = (json: JsonValue): Vocal => ({
  collaboratorDsitUuid: json.collaboratorDsitUuid,
  createdAt: json.createdAt,
  eventUuid: json.eventUuid,
  noteText: json.noteText,
  updatedAt: json.updatedAt,
  uuid: json.uuid,
  llmCallUuid: json.llmCallUuid,
  legalEntityUuid: json.legalEntityUuid,
});

export const vocalsReader = (json: JsonValue): Vocal[] => json.map(vocalReader);

export const collaboratorLeaveReader = (json: JsonValue): CollaboratorLeave => ({
  uuid: json.uuid,
  collaboratorDsitUuid: json.collaboratorDsitUuid,
  startsAt: isoDateReader(json.startsAt),
  endsAt: isoDateReader(json.endsAt),
});

export const collaboratorLeavesReader = (json: JsonValue): CollaboratorLeave[] =>
  list(collaboratorLeaveReader)(json);

export const calendarEventTimeReader = (json: JsonValue): CalendarEventTime => ({
  dateTime: json.dateTime ?? null,
  date: json.date ?? null,
  timeZone: json.timeZone ?? null,
});

export const calendarEventAttendeeReader = (json: JsonValue): CalendarEventAttendee => ({
  email: json.email ?? null,
  displayName: json.displayName ?? null,
  responseStatus: json.responseStatus ?? null,
  isSelf: json.isSelf ?? false,
});

export const calendarEventReader = (json: JsonValue): CalendarEvent => {
  const attendees: CalendarEventAttendee[] = json.attendees
    ? list(calendarEventAttendeeReader)(json.attendees)
    : [];

  // Derive selfResponseStatus from the attendee where isSelf is true
  const selfAttendee = attendees.find(a => a.isSelf);
  const selfResponseStatus = selfAttendee?.responseStatus ?? null;

  return {
    id: json.id,
    summary: json.summary ?? null,
    description: json.description ?? null,
    location: json.location ?? null,
    start: json.start ? calendarEventTimeReader(json.start) : null,
    end: json.end ? calendarEventTimeReader(json.end) : null,
    htmlLink: json.htmlLink ?? null,
    eventType: json.eventType ?? null,
    transparency: json.transparency ?? null,
    status: json.status ?? null,
    attendees,
    selfResponseStatus,
  };
};

export const calendarEventsReader = (json: JsonValue): CalendarEvent[] =>
  list(calendarEventReader)(json);
